import { AnimatePresence, motion } from "framer-motion";
import { type FC, useMemo } from "react";

import type { BlackboardInstance } from "./blackboardModels";
import type { OrgChannelSummary } from "./useOrgChannels";

import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { useOrgMcpServers } from "../../hooks/useOrgMcpServers";
import {
  buildActorChannelGroups,
  type ActorFleetGroup,
} from "./actorFleetGrouping";
import { InstanceCard } from "./InstanceCard";
import { WakeDeliveryChip } from "./WakeDeliveryChip";

interface ActorFleetGroupedProps {
  focusInstanceId: string | null;
  generatingInstances: ReadonlySet<string>;
  highlightedId: string | null;
  /** Actor currently open in the Inspector (base slot) — draws the card ring. */
  inspectedInstanceId: string | null;
  instances: readonly BlackboardInstance[];
  /** The shared channel currently open in the Inspector (`?inspect=channel:`).
   *  Drives the group selection ring. */
  inspectedChannelId: string | null;
  nowMs?: number;
  onCardRef: (instanceId: string, element: HTMLDivElement | null) => void;
  onSelectInstance: (instanceId: string) => void;
  orgChannelsById: ReadonlyMap<string, OrgChannelSummary>;
  /** Open a shared channel in the unified Inspector (`?inspect=channel:<id>`).
   *  The clickable group header is the affordance — Fleet inspects the channel
   *  in place rather than navigating to a separate page. */
  onInspectChannel: (channelId: string) => void;
}

export const ActorFleetGrouped: FC<ActorFleetGroupedProps> = ({
  focusInstanceId,
  generatingInstances,
  highlightedId,
  inspectedInstanceId,
  instances,
  inspectedChannelId,
  nowMs = Date.now(),
  onCardRef,
  onSelectInstance,
  orgChannelsById,
  onInspectChannel,
}) => {
  const mcpAuth = useDashboardAuth();
  const { servers: mcpServers } = useOrgMcpServers(mcpAuth.organizationId);
  // One lookup for the whole fleet: a per-card hook would issue one request per
  // actor on a page that exists to show many.
  const mcpServerNames = useMemo(
    () => new Map(mcpServers.map((server) => [server.id, server.display_name])),
    [mcpServers],
  );
  const groups = buildActorChannelGroups(instances, orgChannelsById);
  return (
    <div className="relative z-10 space-y-4">
      {groups.map((group) => (
        <ActorFleetSection
          mcpServerNames={mcpServerNames}
          focusInstanceId={focusInstanceId}
          generatingInstances={generatingInstances}
          group={group}
          highlightedId={highlightedId}
          inspectedInstanceId={inspectedInstanceId}
          inspectedChannelId={inspectedChannelId}
          key={groupKey(group)}
          nowMs={nowMs}
          onCardRef={onCardRef}
          onSelectInstance={onSelectInstance}
          onInspectChannel={onInspectChannel}
        />
      ))}
    </div>
  );
};

const ActorFleetSection: FC<{
  mcpServerNames: ReadonlyMap<string, string>;
  focusInstanceId: string | null;
  generatingInstances: ReadonlySet<string>;
  group: ActorFleetGroup;
  highlightedId: string | null;
  inspectedInstanceId: string | null;
  inspectedChannelId: string | null;
  nowMs: number;
  onCardRef: (instanceId: string, element: HTMLDivElement | null) => void;
  onSelectInstance: (instanceId: string) => void;
  onInspectChannel: (channelId: string) => void;
}> = ({
  mcpServerNames,
  focusInstanceId,
  generatingInstances,
  group,
  highlightedId,
  inspectedInstanceId,
  inspectedChannelId,
  nowMs,
  onCardRef,
  onSelectInstance,
  onInspectChannel,
}) => {
  // The workspace (chat/repos/env/artifacts) is a property of the shared
  // channel and opens in the unified Inspector. The group header IS the
  // affordance: clicking it inspects the channel in place.
  const channelId = group.kind === "shared" ? group.channel.id : null;
  const isInspected = channelId !== null && channelId === inspectedChannelId;
  const headerLabel = groupHeaderLabel(group);

  return (
    <section
      className={`border bg-slate-100/40 p-2 transition-colors dark:bg-slate-950/20 ${
        isInspected
          ? "border-emerald-500/70 ring-1 ring-emerald-500/40 dark:border-emerald-400/50"
          : "border-emerald-500/25 dark:border-emerald-400/10"
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-emerald-500/25 px-1 pb-2 font-mono dark:border-emerald-400/10">
        {channelId ? (
          <button
            type="button"
            onClick={() => onInspectChannel(channelId)}
            aria-pressed={isInspected}
            title="Inspect channel"
            className="group inline-flex min-w-0 items-center gap-1 text-left text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-700/90 transition-colors hover:text-emerald-600 dark:text-emerald-300/85 dark:hover:text-emerald-200"
          >
            <span className="min-w-0 truncate">{headerLabel}</span>
            <span
              aria-hidden
              className="text-[8px] text-emerald-600/50 transition-transform group-hover:translate-x-0.5 dark:text-emerald-400/50"
            >
              →
            </span>
          </button>
        ) : (
          <p className="min-w-0 truncate text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-700/90 dark:text-emerald-300/85">
            {headerLabel}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
          <WakeDeliveryChip
            summary={group.kind === "shared" ? group.summary : null}
          />
          <span>{groupHeaderMeta(group, nowMs)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <AnimatePresence initial={false}>
          {group.actors.map((instance) => {
            const isFocus = instance.id === focusInstanceId;
            const isInspectedActor = instance.id === inspectedInstanceId;
            return (
              <motion.div
                key={instance.id}
                layout="position"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{
                  duration: 0.25,
                  ease: "easeOut",
                  layout: { duration: 0.35, ease: "easeOut" },
                }}
                className={`${
                  isFocus ? "sm:col-span-2 lg:col-span-2 xl:col-span-2" : ""
                } ${
                  isInspectedActor ? "ring-1 ring-emerald-500/40" : ""
                }`.trim()}
                ref={(element) => onCardRef(instance.id, element)}
              >
                <InstanceCard
                  mcpServerNames={mcpServerNames}
                  highlighted={highlightedId === instance.id}
                  instance={instance}
                  isGenerating={generatingInstances.has(instance.id)}
                  onClick={() => onSelectInstance(instance.id)}
                  variant={isFocus ? "focus" : "compact"}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </section>
  );
};

export function groupHeaderLabel(group: ActorFleetGroup): string {
  if (group.kind === "unassigned") {
    return "[ UNASSIGNED ]";
  }
  const displayName = group.summary?.displayName ?? group.channel.displayName;
  const provenance = group.summary?.provenance ?? group.channel.provenance;
  const channelLabel =
    provenance === "teams_bind" ? displayName : `#${displayName}`;
  if (group.summary?.activeTeamsConversationLabel) {
    return `[ ${channelLabel} › ${group.summary.activeTeamsConversationLabel} ]`;
  }
  return `[ ${channelLabel} ]`;
}

export function groupHeaderMeta(group: ActorFleetGroup, nowMs: number): string {
  const actorCount = group.actors.length;
  if (group.kind === "unassigned") {
    return `${actorCount} ${pluralize(actorCount, "actor")}`;
  }
  if (
    group.summary?.hasDetachedTeamsBinding === true &&
    !group.summary.activeTeamsBindingId
  ) {
    return `${actorCount} ${pluralize(actorCount, "member")} · binding detached`;
  }
  return `${actorCount} ${pluralize(actorCount, "member")} · last activity ${formatRelativeActivity(
    group.summary?.lastActivityAt ?? null,
    nowMs,
  )}`;
}

function groupKey(group: ActorFleetGroup): string {
  return group.kind === "unassigned"
    ? "unassigned"
    : `shared:${group.channel.id}`;
}

function pluralize(count: number, singular: string): string {
  return count === 1 ? singular : `${singular}s`;
}

function formatRelativeActivity(value: string | null, nowMs: number): string {
  if (!value) {
    return "—";
  }
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return "—";
  }
  const elapsedSeconds = Math.max(0, Math.floor((nowMs - timestamp) / 1000));
  if (elapsedSeconds < 60) {
    return "just now";
  }
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m ago`;
  }
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `${elapsedHours}h ago`;
  }
  return `${Math.floor(elapsedHours / 24)}d ago`;
}
